<template>
  <div class="frisbuy-story-slider" v-touch:swipe="swipeAlbum">
    <div
      class="frisbuy-story-slider__transition"
      :style="{ transform: translateX }"
    >
      <div
        v-for="(albumItem, albumIndex) in albums"
        :key="albumItem.id"
        class="frisbuy-album-slide__wrapper"
      >
        <album-slide
          class="frisbuy-slide"
          :class="{
            'frisbuy-slide--current': albumIndex === selectedAlbumIndex,
          }"
          :isSelectedAlbum="albumIndex === selectedAlbumIndex"
          :album="albumItem"
          :selectedPostIndex="
            /*
            NOTE: `albumIndex` is NOT reactive (In Vue 3). And prop doesn't know about changes `runningPostIndex`.
            So I get `runningPostIndex` from current album - `album`
          */
            albumIndex === selectedAlbumIndex
              ? album.runningPostIndex
              : albumItem.runningPostIndex || 0
          "
          ref="slides"
          @change-album="changeAlbum(albumIndex)"
          @resume-slide="play()"
          @click.self.stop
          @navigationTouchPause="$emit('navigationTouchPause')"
          @navigationSwitchToNext="(step) => changeSlide(step)"
          @pauseSlide="() => pause()"
          @playSlide="() => play()"
          @getCountOfActiveQueries="(count) => (hasActiveQuery = count > 0)"
          :playWithSound="playWithSound"
        ></album-slide>
      </div>
    </div>
  </div>
</template>

<script>
import { toRaw } from "vue";
import albumSlide from "./albumSlide.vue";

export default {
  data() {
    return {
      stepTransition: 0,
      progressTransition: 0,
      progressTimeout: null,
      isPause: false,

      hasActiveQuery: false,
    };
  },

  props: ["albums", "album", "openedGoods", "playWithSound"],

  emits: [
    "getSlideProgress",
    "playSlide",
    "pauseSlide",
    "navigationSwitchToNext",
    "navigationTouchPause",
    "change-current",
    "swipeAlbum",
  ],

  components: {
    albumSlide,
  },

  computed: {
    selectedAlbumIndex() {
      const index = this.albums.indexOf(toRaw(this.album));

      return index;
    },

    currentSlide() {
      const { album, $refs } = this;
      const { slides } = $refs;

      const [firstSlide] = slides;

      return (
        slides.find(
          (slide) =>
            album && slide.slide === toRaw(album.posts[album.runningPostIndex])
        ) || firstSlide
      );
    },

    nextSlide() {
      const { currentSlide, $refs } = this;
      const { slides } = $refs;

      return slides[slides.indexOf(currentSlide) + 1];
    },

    slideIndex() {
      return (this.albums && this.albums.indexOf(toRaw(this.album))) || 0;
    },

    translateX: {
      get() {
        return `
          translateX(calc(
            50vw - (var(--frisbuy-story-slide-wrapper-width) / 2) - (var(--frisbuy-story-slide-wrapper-width) * ${this.slideIndex})
          ))
        `;
      },
      cache: false,
    },

    isTablet: {
      cache: false,
      get() {
        return Math.min(window.innerWidth, screen.width) < 768;
      },
    },
  },

  methods: {
    loadCurrentSlide() {
      this.currentSlide.load();
    },

    loadAdjacentSlides(countAdjacents = 1) {
      if (countAdjacents < 0) {
        console.error("'countAdjacents' cannot be less than 0");
        return;
      }

      const slides = this.$refs.slides;
      const adjacentsAlbumsIndexes = [];

      // [START] add adjacents albums
      for (let i = 1; i <= countAdjacents; i++) {
        const prevAlbumIndex = this.selectedAlbumIndex - i;
        const nextAlbumIndex = this.selectedAlbumIndex + i;

        if (!slides[prevAlbumIndex] && !slides[nextAlbumIndex]) {
          break;
        }

        if (slides[prevAlbumIndex]) {
          adjacentsAlbumsIndexes.push(prevAlbumIndex);
        }

        if (slides[nextAlbumIndex]) {
          adjacentsAlbumsIndexes.push(nextAlbumIndex);
        }
      }
      // [END] add adjacents albums

      // [START] load adjacent albums
      if (slides && adjacentsAlbumsIndexes.length > 0) {
        adjacentsAlbumsIndexes.forEach((albumIndex) => {
          /*
            TODO: refactor
            This component shouldn't use inner child component's var contentIsLoaded
          */
          if (!slides[albumIndex].contentIsLoaded) {
            slides[albumIndex].load();
          }
        });
      }
      // [END] load adjacent albums
    },

    changeSlide(step) {
      this.stop();
      this.$emit("navigationSwitchToNext", step);
    },

    changeAlbum(index) {
      this.stop();

      this.$emit("change-current", index);

      this.$nextTick(() => {
        this.loadCurrentSlide();
      });
    },

    swipeAlbum(direction) {
      if (["left", "right"].includes(direction)) {
        this.stop();
      }

      this.$emit("swipeAlbum", direction);
    },

    runTransition() {
      let progress = this.progressTransition + this.stepTransition;

      if (progress >= 100) {
        progress = 100;
      }

      this.$emit("getSlideProgress", progress);
      this.progressTransition = progress;

      clearTimeout(this.progressTimeout);

      this.progressTimeout = setTimeout(() => {
        if (this.progressTransition >= 100) {
          this.$emit("navigationSwitchToNext", 1);
        } else {
          this.runTransition();
        }
      }, 1000);
    },

    setTransition(duration) {
      this.stepTransition = 100 / (duration || 5);
      this.progressTransition = 0;
      this.$emit("getSlideProgress", this.progressTransition);

      setTimeout(() => {
        this.runTransition();
      }, 0);
    },

    async play() {
      await this.$nextTick();

      const video = this.currentSlide.$refs.video;

      if (this.isPause) {
        if (video) {
          let videoProgress = (video.currentTime / video.duration) * 100;
          if (isNaN(videoProgress)) {
            videoProgress = 0;
          }
          this.progressTransition = videoProgress;
        } else {
          let slideImageProgress =
            this.progressTransition - this.stepTransition;
          this.progressTransition =
            slideImageProgress >= 0 ? slideImageProgress : 0;
        }

        this.isPause = false;
        this.$emit("playSlide");

        this.currentSlide.play();
        this.runTransition();

        return;
      }

      try {
        this.currentSlide.play(this.setTransition);
      } catch (e) {
        console.log("Error", e.message);
      }
    },

    stop() {
      if (this.progressTransition >= 100) {
        this.$emit("playSlide");
        return;
      }

      this.currentSlide.stop();
      this.progressTransition = 0;
      clearTimeout(this.progressTimeout);
      this.isPause = false;
    },

    pause() {
      if (this.progressTransition >= 100) {
        this.$emit("playSlide");
        return;
      }

      clearTimeout(this.progressTimeout);
      this.currentSlide.pause();
      this.isPause = true;

      this.$emit("pauseSlide");
    },

    touchPause() {
      const playHandler = () => {
        const { openedGoods } = this;

        if (openedGoods) return;

        this.play();

        document.removeEventListener("mouseup", playHandler);
        document.removeEventListener("touchend", playHandler);
      };

      document.addEventListener("mouseup", playHandler);
      document.addEventListener("touchend", playHandler);

      this.pause();
    },
  },

  mounted() {
    this.$parent.isSliderOpen = true;

    this.loadCurrentSlide();
    this.loadAdjacentSlides(1);
  },

  watch: {
    hasActiveQuery(value) {
      if (value) {
        if (!this.isPause) {
          this.pause();
        }
      } else {
        setTimeout(() => {
          if (!this.hasActiveQuery && this.isPause) {
            this.play();
          }
        }, 2000);
      }
    },

    album() {
      this.loadAdjacentSlides(1);
    },

    "album.runningPostIndex": {
      handler() {
        this.progressTransition = 0;

        if (this.progressTimeout) {
          clearTimeout(this.progressTimeout);
        }

        this.$nextTick(() => this.currentSlide.load());
      },
      immediate: false,
    },
  },
};
</script>

<style lang="scss">
.frisbuy-story-slider {
  --frisbuy-story-slide-wrapper-width: var(--frisbuy-story-slide-width);

  vertical-align: baseline;
  position: absolute;
  inset: 50% 0 0;
  transform: translateY(-50%);
  height: fit-content;

  .frisbuy-story-slider__transition {
    vertical-align: baseline;
    display: flex;
    width: fit-content;
    height: 100%;
    transition: transform 0.25s ease-out;
    will-change: transform;
    align-items: center;

    .frisbuy-album-slide__wrapper {
      display: flex;
      justify-content: center;

      width: var(--frisbuy-story-slide-wrapper-width);
    }

    @media screen and (max-width: 767px) {
      --frisbuy-story-slide-wrapper-width: 100dvw;

      height: 100dvh;
      transition: transform 0.4s ease-out;
    }

    .frisbuy-slide {
      background-color: rgba(0, 0, 0, 0.8);
      @media (min-width: 768px) {
        border-radius: 10px;
      }

      overflow: hidden;
    }
  }
}

.frisbuy-story-slide {
  width: 100%;
  height: 100%;
  background-position: center;
  background-size: contain;
  background-repeat: no-repeat;
}
</style>
